// src/pages/TeamMeetingDetail.js
// Detailed view of a single team meeting report

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { MEETING_INVENTORY, getMeetingDurationMs } from '../utils/metrics';
import PageLoading from '../components/PageLoading';

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDuration(ms) {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function parseShortcodes(text) {
  if (!text) return text;
  
  // Replace shortcodes with display versions
  return text
    .replace(/\/s/g, '[Section]')
    .replace(/\/t/g, '[Topic]')
    .replace(/\/q/g, '[Question]')
    .replace(/\/i/g, '[Input]')
    .replace(/\/j/g, '[Joke]')
    .replace(/\/h/g, '[Help]')
    .replace(/\/m/g, '[Mistake]')
    .replace(/\/w/g, '[Weakness]')
    .replace(/\/c/g, '[Note]')
    .replace(/\/1/g, '[Purpose]')
    .replace(/\/2/g, '[Values]')
    .replace(/\/3/g, '[Business]')
    .replace(/\/4/g, '[Strategic]')
    .replace(/\/5/g, '[Goals]')
    .replace(/\/6/g, '[Roles]')
    .replace(/`w/g, '[Wonder]')
    .replace(/`i/g, '[Invention]')
    .replace(/`d/g, '[Discernment]')
    .replace(/`g/g, '[Galvanizing]')
    .replace(/`e/g, '[Enablement]')
    .replace(/`t/g, '[Tenacity]');
}

export default function TeamMeetingDetail() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  const teamId = userProfile?.teamId;

  useEffect(() => {
    if (meetingId) loadMeeting();
  }, [meetingId]);

  async function loadMeeting() {
    try {
      console.log('🔍 Loading meeting:', meetingId);

      const meetingSnap = await getDoc(doc(db, 'meetings', meetingId));
      if (meetingSnap.exists()) {
        const meetingData = { id: meetingSnap.id, ...meetingSnap.data() };
        setMeeting(meetingData);
        console.log('✓ Meeting loaded:', meetingData.name);
      } else {
        console.error('❌ Meeting not found');
      }
    } catch (err) {
      console.error('❌ Error loading meeting:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <PageLoading text="Loading meeting..." />;
  }

  if (!meeting) {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>Meeting Not Found</h2>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/team-meetings')}>
          Back to Meetings
        </button>
      </div>
    );
  }

  const entryCount = meeting.entries?.length || 0;
  const inventoryChecks = meeting.inventoryChecks || {};

  // Count observations by category (covers both old sp1–sp10 and new sp11+ item ids)
  const countChecks = prefix =>
    Object.entries(inventoryChecks).filter(([id, v]) => v && new RegExp(`^${prefix}\\d+$`).test(id)).length;
  const simpleProcessCount = countChecks('sp');
  const compellingContentCount = countChecks('cc');
  const healthyBehaviorsCount = countChecks('hb');
  const rightGeniusCount = countChecks('rg');
  const totalObservations = simpleProcessCount + compellingContentCount + healthyBehaviorsCount + rightGeniusCount;

  // Meetings recorded before the July 2026 inventory revision had 10 items per
  // category; detect them by the presence of any id not in the current set.
  const currentItemIds = new Set(Object.values(MEETING_INVENTORY).flatMap(g => g.items.map(i => i.id)));
  const isLegacyInventory = Object.keys(inventoryChecks).some(id => !currentItemIds.has(id));
  const perCategoryMax = isLegacyInventory ? 10 : MEETING_INVENTORY.simpleProcess.items.length;

  // Group entries by speaker
  const speakers = meeting.entries?.reduce((acc, entry) => {
    const name = entry.speakerName || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(entry);
    return acc;
  }, {}) || {};

  return (
    <div className="fade-up">
      {/* Header with back button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate('/team-meetings')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: '1.2rem',
          }}
        >
          ← Back
        </button>
      </div>

      {/* Meeting Header */}
      <div className="page-header">
        <h2>{meeting.name}</h2>
        <p>{formatDate(meeting.meetingDate || meeting.createdAt)}</p>
      </div>

      {/* Consultant info */}
      {meeting.consultantName && (
        <div
          style={{
            padding: 16,
            background: 'var(--bg-soft)',
            borderRadius: 8,
            marginBottom: 24,
            fontSize: '0.9rem',
            color: 'var(--ink-muted)',
          }}
        >
          <strong>Consultant:</strong> {meeting.consultantName}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid-3" style={{ marginBottom: 32 }}>
        <div className="stat-card">
          <div className="stat-label">Duration</div>
          <div className="stat-value">{formatDuration(getMeetingDurationMs(meeting))}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Conversation Entries</div>
          <div className="stat-value">{entryCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Attendees</div>
          <div className="stat-value">{meeting.attendees?.length || 0}</div>
        </div>
      </div>

      {/* Observations Summary */}
      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-title" style={{ marginBottom: 20 }}>Consultant Observations</div>
        <div className="grid-2" style={{ marginBottom: 20 }}>
          <div
            style={{
              padding: 16,
              background: 'var(--bg-soft)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Compelling Content</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
              {compellingContentCount}/{perCategoryMax}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>behaviors observed</div>
          </div>
          <div
            style={{
              padding: 16,
              background: 'var(--bg-soft)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Healthy Behaviors</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
              {healthyBehaviorsCount}/{perCategoryMax}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>behaviors observed</div>
          </div>
          <div
            style={{
              padding: 16,
              background: 'var(--bg-soft)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Right Chemistry</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
              {rightGeniusCount}/{perCategoryMax}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>behaviors observed</div>
          </div>
          <div
            style={{
              padding: 16,
              background: 'var(--bg-soft)',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>Simple Process</div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
              {simpleProcessCount}/{perCategoryMax}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>behaviors observed</div>
          </div>
        </div>
        <div
          style={{
            padding: 12,
            background: 'var(--border)',
            borderRadius: 8,
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          Total Observations: {totalObservations}/{perCategoryMax * 4}
        </div>
      </div>

      {/* Attendees */}
      {meeting.attendees && meeting.attendees.length > 0 && (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>Attendees</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {meeting.attendees.map((attendee, idx) => (
              <span
                key={idx}
                className="badge badge-blue"
                style={{ padding: '6px 10px', fontSize: '0.85rem' }}
              >
                {attendee.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Conversation Entries */}
      {meeting.entries && meeting.entries.length > 0 && (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-title" style={{ marginBottom: 20 }}>Conversation Flow</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {meeting.entries.map((entry, idx) => (
              <div
                key={entry.id || idx}
                style={{
                  padding: 16,
                  background: 'var(--bg-soft)',
                  borderRadius: 8,
                  borderLeft: `4px solid var(--accent)`,
                }}
              >
                <div
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--accent)',
                    marginBottom: 8,
                  }}
                >
                  {entry.speakerName}
                </div>
                <div
                  style={{
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    color: 'var(--ink)',
                  }}
                >
                  {parseShortcodes(entry.text)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Speaker Summary */}
      {Object.keys(speakers).length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Speaker Summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {Object.entries(speakers).map(([name, entries]) => (
              <div
                key={name}
                style={{
                  padding: 12,
                  background: 'var(--bg-soft)',
                  borderRadius: 8,
                }}
              >
                <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 8 }}>
                  {name}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
                  {entries.length} speaking turn{entries.length !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
